import { Stack } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { EnergyAnalysisDimension, EnergyAnalysisYearRange } from "../model/types";

import { useCrremEnergyInputUpload } from "../hooks/useCrremEnergyInputUpload";
import { useEnergyAnalysisDashboard } from "../hooks/useEnergyAnalysisDashboard";
import { ENERGY_ANALYSIS_DIMENSION } from "../model/constants";
import { EnergyAnalysisDashboardBody } from "./EnergyAnalysisDashboardBody";
import { EnergyAnalysisDimensionControl } from "./EnergyAnalysisDimensionControl";
import { EnergyAnalysisSettings } from "./EnergyAnalysisSettings";

type EnergyAnalysisDashboardContentProps = {
  projectId: string;
};

export const EnergyAnalysisDashboardContent = ({ projectId }: EnergyAnalysisDashboardContentProps) => {
  const { t } = useTranslation();
  const [dimension, setDimension] = useState<EnergyAnalysisDimension>(ENERGY_ANALYSIS_DIMENSION.joint);
  const [snapshotYear, setSnapshotYear] = useState<number | undefined>();
  const [yearRange, setYearRange] = useState<EnergyAnalysisYearRange | undefined>();
  const dashboard = useEnergyAnalysisDashboard(
    projectId,
    dimension,
    t("BuildingProfile.energyAnalysis.unit"),
    yearRange,
    snapshotYear
  );
  const { isUploading } = useCrremEnergyInputUpload(projectId);
  const { settings, viewModel } = dashboard;
  const isBusy = dashboard.isLoading || isUploading;
  return (
    <Stack className="h-full flex-col items-stretch bg-lds-surface-muted">
      <div className="mx-6 mt-6 rounded-ds-0-5x bg-lds-surface-bright px-5 py-3 pr-10 ">
        <Stack className="w-full flex-col items-stretch gap-6 p-1 px-2 shadow-md">
          <Stack className="w-full flex-wrap items-center gap-10">
            <h2 className="font-ds-headline-lg shrink-0 text-left text-lds-foreground-heading-active">
              {t("BuildingProfile.energyAnalysis.title")}
            </h2>
            <EnergyAnalysisDimensionControl dimension={dimension} onChange={setDimension} />
          </Stack>
          <EnergyAnalysisSettings
            disabled={isBusy}
            hideCorrectionSelection={dimension === ENERGY_ANALYSIS_DIMENSION.corrections}
            hideMetricSelection={dimension === ENERGY_ANALYSIS_DIMENSION.joint}
            onSave={dashboard.saveSettings}
            onScaleChange={dashboard.setScale}
            onSnapshotYearChange={setSnapshotYear}
            onYearRangeChange={(nextRange) => {
              setYearRange(nextRange);
              setSnapshotYear((currentYear) =>
                currentYear !== undefined &&
                currentYear >= nextRange[0] &&
                currentYear <= nextRange[1]
                  ? currentYear
                  : nextRange[1]
              );
            }}
            scale={dashboard.scale}
            settings={settings}
            snapshotYear={viewModel.referenceYear}
            snapshotYears={viewModel.years}
            yearRange={yearRange ?? dashboard.yearRange}
            years={dashboard.availableYears}
          />
        </Stack>
      </div>
      <div className="flex-1 overflow-auto px-6 py-4 text-left">
        <EnergyAnalysisDashboardBody
          dimension={dimension}
          isEmpty={dashboard.isEmpty}
          isLoading={isBusy}
          payload={dashboard.dashboardPayload}
          rows={dashboard.filteredRows}
          settings={settings}
          viewModel={viewModel}
        />
      </div>
    </Stack>
  );
};
